import React, { useState, useEffect } from 'react';
import { Seo } from '../seo/Seo';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { 
  Flame, 
  Heart, 
  Star, 
  Shield, 
  Zap, 
  Users, 
  MessageCircle, 
  Camera, 
  MapPin,
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Award,
  AlertCircle,
  X
} from 'lucide-react';
import { checkZipCodeSupport } from '../utils/zipCodeChecker';
import { LandingPremiumSection } from './PublicPages';
import { BrandLogo, BrandLockup } from './BrandLogo';
import { HeroStage, PhotoPanel, PersonAvatar } from './PromoPhotos';

const LandingPage = ({ onGetStarted, onViewCities, onFootnoteClick }) => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [zipCode, setZipCode] = useState('');
  const [zipCodeStatus, setZipCodeStatus] = useState(null); // null, 'supported', 'not-supported'
  const [showZipError, setShowZipError] = useState(false);
  const [supportedCity, setSupportedCity] = useState('');


  const handleZipCodeCheck = () => {
    if (!zipCode || zipCode.trim().length < 3) {
      setShowZipError(true);
      setZipCodeStatus(null);
      return;
    }

    const result = checkZipCodeSupport(zipCode.trim());
    
    if (result.isSupported) {
      setZipCodeStatus('supported');
      setSupportedCity(result.city);
      setShowZipError(false);
      // Wait a moment to show success, then proceed to signup
      setTimeout(() => {
        onGetStarted();
      }, 1500);
    } else {
      setZipCodeStatus('not-supported');
      setShowZipError(false);
    }
  };

  const handleZipCodeChange = (e) => {
    const value = e.target.value.toUpperCase().trim(); // Allow alphanumeric, convert to uppercase
    setZipCode(value);
    setZipCodeStatus(null);
    setShowZipError(false);
  };

  const resetZipCodeCheck = () => {
    setZipCodeStatus(null);
    setZipCode('');
    setShowZipError(false);
    setSupportedCity('');
  };


  const features = [
    {
      icon: Heart,
      title: "Smart Matching",
      description: "Advanced AI algorithms analyze compatibility based on interests, values, and lifestyle preferences for meaningful connections.",
      color: "from-pink-500 to-red-500"
    },
    {
      icon: Star,
      title: "Compatibility Scoring",
      description: "See your compatibility percentage with potential matches based on 20+ factors including interests, age, and location.",
      color: "from-blue-500 to-indigo-500"
    },
    {
      icon: Shield,
      title: "Verified Profiles",
      description: "Photo verification and identity checks ensure authentic profiles and safer dating experiences for all users.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: Camera,
      title: "Video Profiles",
      description: "Showcase your personality with video introductions and see the real person behind the photos.",
      color: "from-purple-500 to-violet-500"
    },
    {
      icon: MessageCircle,
      title: "Enhanced Messaging",
      description: "Voice messages, photo sharing, and advanced chat features make conversations more engaging and authentic.",
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: MapPin,
      title: "Global Cities",
      description: "Available in 50 major cities across North America, Asia, and the Pacific for worldwide connections.",
      color: "from-teal-500 to-cyan-500"
    }
  ];

  const stats = [
    { value: "Verified", label: "Profile Verification", icon: Users },
    { value: "Smart", label: "Compatibility Matching", icon: Heart },
    { value: "Private", label: "Secure Messaging", icon: Award },
    { value: "50+", label: "Supported Cities", icon: MapPin }
  ];

  const testimonials = [
    {
      name: "Sarah M.",
      location: "New York, NY",
      text: "FlameLink's compatibility scoring helped me find someone truly compatible. We've been together for 8 months now!",
      personId: "sarah-m"
    },
    {
      name: "Michael R.",
      location: "San Francisco, CA",
      text: "The video profiles feature made all the difference. I could see personalities before matching, leading to better connections.",
      personId: "michael-r"
    },
    {
      name: "Emma L.",
      location: "Chicago, IL",
      text: "Finally, a dating app that prioritizes quality over quantity. The verified profiles give me confidence in every match.",
      personId: "emma-l"
    },
    {
      name: "Jessica K.",
      location: "Honolulu, HI", 
      text: "The tipping feature is brilliant! It really helps break the ice and shows genuine interest. Found my perfect match here!",
      personId: "jessica-k"
    },
    {
      name: "Amanda S.",
      location: "Miami, FL",
      text: "Love the global cities feature! Met someone amazing while traveling. FlameLink made our connection possible across continents.",
      personId: "amanda-s"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50">
      <Seo path="/" />
      {/* Navigation */}
      <nav className="relative z-50 bg-white/80 backdrop-blur-md border-b border-pink-100 sticky top-0">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <BrandLockup size="md" testId="landing-brand-lockup" />
            
            <div className="hidden lg:flex items-center space-x-6">
              <a href="#features" className="text-gray-700 hover:text-red-500 transition-colors font-medium">Features</a>
              <a href="#how-it-works" className="text-gray-700 hover:text-red-500 transition-colors font-medium">How It Works</a>
              <button 
                onClick={onViewCities} data-testid="nav-cities-btn"
                className="text-gray-700 hover:text-red-500 transition-colors font-medium"
              >
                Supported Cities
              </button>
              <a href="#testimonials" className="text-gray-700 hover:text-red-500 transition-colors font-medium">Success Stories</a>
              <a href="#premium" className="text-gray-700 hover:text-red-500 transition-colors font-medium" data-testid="nav-premium-link">Premium</a>
              <Button
                onClick={onGetStarted} data-testid="nav-get-started-btn"
                className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-6 py-2 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Get Started
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Mobile menu button */}
            <Button
              onClick={onGetStarted} data-testid="nav-mobile-get-started-btn"
              className="lg:hidden bg-gradient-to-r from-red-500 to-pink-500 text-white"
            >
              Join Now
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-10 pb-16 lg:pt-14 lg:pb-24 bg-[radial-gradient(ellipse_at_top,_#fde2e4_0%,_#fbcfe8_35%,_#fdf2f8_70%,_#fff7ed_100%)]" data-testid="hero-section">
        <div className="relative container mx-auto px-6">
          <HeroStage>
            <Badge className="mb-5 bg-white/80 text-rose-700 border-rose-200 px-4 py-2 shadow-sm">
              <TrendingUp className="w-4 h-4 mr-2" />
              Dating Built for Meaningful Connections
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-5 leading-tight" data-testid="hero-headline">
              <span className="bg-gradient-to-r from-amber-500 via-red-600 to-pink-600 bg-clip-text text-transparent">Find Your</span>
              <br />
              <span className="bg-gradient-to-r from-fuchsia-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">Perfect Match</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-700 mb-7 max-w-xl mx-auto leading-relaxed" data-testid="hero-subtext">
              Experience the next generation of dating with <span className="font-semibold text-red-600">AI-powered compatibility</span>,
              <span className="font-semibold text-blue-600"> verified profiles</span>, and
              <span className="font-semibold text-green-600"> meaningful connections</span> in major cities worldwide.
            </p>
            <div className="flex justify-center">
              <Button
                onClick={onGetStarted}
                data-testid="hero-cta-btn"
                className="bg-gradient-to-r from-red-500 via-pink-500 to-orange-500 hover:from-red-600 hover:via-pink-600 hover:to-orange-600 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-2xl"
              >
                <Zap className="w-5 h-5 mr-2" />
                Start Dating Today
              </Button>
            </div>
          </HeroStage>
          <div className="h-12 lg:h-16" />
          <div className="text-center max-w-4xl mx-auto">
            {/* Zip Code Check Section */}
            <div className="max-w-md mx-auto mb-12">
              <Card className="p-6 bg-white/90 backdrop-blur-sm border-2 border-pink-100 shadow-xl">
                <div className="text-center mb-4">
                  <MapPin className="w-6 h-6 mx-auto mb-2 text-red-500" />
                  <h3 className="text-lg font-semibold text-gray-900">Check Your Area</h3>
                  <p className="text-sm text-gray-600">Enter your ZIP code or postal code to see if FlameLink is available in your city</p>
                </div>

                {zipCodeStatus === null && (
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        value={zipCode}
                        onChange={handleZipCodeChange}
                        placeholder="Enter ZIP/postal code"
                        className="flex-1 text-center text-lg font-semibold"
                      />
                      <Button
                        onClick={handleZipCodeCheck}
                        className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-6"
                        disabled={zipCode.trim().length < 3}
                      >
                        Check
                      </Button>
                    </div>
                    
                    {showZipError && (
                      <div className="flex items-center justify-center text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        Please enter a valid ZIP code or postal code (at least 3 characters)
                      </div>
                    )}
                  </div>
                )}

                {zipCodeStatus === 'supported' && (
                  <div className="text-center">
                    <div className="flex items-center justify-center text-green-600 mb-3">
                      <CheckCircle className="w-6 h-6 mr-2" />
                      <span className="font-semibold">Great news!</span>
                    </div>
                    <p className="text-gray-700 mb-3">
                      FlameLink is available in <span className="font-semibold text-green-600">{supportedCity}</span>
                    </p>
                    <p className="text-sm text-gray-600">Redirecting you to sign up...</p>
                    <div className="mt-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500 mx-auto"></div>
                    </div>
                  </div>
                )}

                {zipCodeStatus === 'not-supported' && (
                  <div className="text-center">
                    <div className="flex items-center justify-center text-orange-600 mb-3">
                      <AlertCircle className="w-6 h-6 mr-2" />
                      <span className="font-semibold">Area Not Supported</span>
                    </div>
                    <p className="text-gray-700 mb-4">
                      We're sorry, FlameLink is not yet available in ZIP code <span className="font-semibold">{zipCode}</span>.
                    </p>
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-4 mb-4">
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>Currently available in 50 major cities worldwide.</strong>
                      </p>
                      <button
                        onClick={onViewCities} data-testid="cities-view-all-btn"
                        className="text-red-600 hover:text-red-800 font-semibold text-sm underline"
                      >
                        View all supported cities →
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Thank you for your interest! We're expanding rapidly and hope to serve your area soon.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={resetZipCodeCheck}
                        variant="outline"
                        className="flex-1"
                      >
                        Try Another ZIP
                      </Button>
                      <Button
                        onClick={() => window.open('mailto:support@flamelink.com?subject=Expansion Request', '_blank')}
                        className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white"
                      >
                        Notify Me
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-3xl mx-auto">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="text-center">
                    <Icon className="w-8 h-8 mx-auto mb-2 text-red-500" />
                    <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                    <div className="text-gray-600 font-medium">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white relative overflow-hidden">

        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Revolutionary Features
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Advanced technology meets authentic connections. Discover why FlameLink is changing the dating game.
            </p>
          </div>
          <PhotoPanel ids={['girl103', 'men21', 'girl106']} tone="rose" className="max-w-3xl mx-auto mb-16" testId="features-photo-panel" />

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={index}
                    className={`p-6 cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                      activeFeature === index 
                        ? 'shadow-2xl border-2 border-pink-200 bg-gradient-to-r from-pink-50 to-red-50' 
                        : 'hover:shadow-lg'
                    }`}
                    onClick={() => setActiveFeature(index)}
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`p-3 rounded-lg bg-gradient-to-r ${feature.color}`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-pink-500 via-red-500 to-orange-500 rounded-3xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Enhanced Dating Experience</h3>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 mr-3 text-green-300" />
                    <span>Matches based on shared interests and values</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 mr-3 text-green-300" />
                    <span>AI-powered personality matching</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 mr-3 text-green-300" />
                    <span>Verified profiles for safety</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 mr-3 text-green-300" />
                    <span>Video profiles and voice messages</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50 relative overflow-hidden">

        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                How FlameLink Works
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Simple steps to find your perfect match with advanced technology backing every connection.
            </p>
          </div>
          <PhotoPanel ids={['group8']} tone="indigo" className="max-w-xl mx-auto mb-16" heights="h-[240px] sm:h-[300px]" testId="how-it-works-photo-panel" />

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Create Your Profile",
                description: "Upload photos, share your interests, and complete your personality quiz for better matches.",
                icon: Users,
                color: "from-pink-500 to-red-500"
              },
              {
                step: "02",
                title: "Discover Matches",
                description: "Our AI analyzes compatibility and shows you profiles with the highest match potential.",
                icon: Heart,
                color: "from-blue-500 to-indigo-500"
              },
              {
                step: "03",
                title: "Connect & Date",
                description: "Start meaningful conversations and plan dates with verified, compatible matches.",
                icon: MessageCircle,
                color: "from-green-500 to-emerald-500"
              }
            ].map((step, index) => {
              const Icon = step.icon;
              return (
                <Card key={index} className="p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                  <div className={`w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-gray-400 mb-2">{step.step}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                Success Stories
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real people finding real love through FlameLink's advanced matching technology.
            </p>
            <p className="mt-3 text-sm text-gray-500 max-w-2xl mx-auto" data-testid="success-stories-disclosure">
              Illustrative examples — names and photos are promotional models, not member testimonials.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <Card key={index} className="p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <div className="flex items-center mb-4">
                  <PersonAvatar personId={testimonial.personId} className="w-20 h-20 sm:w-24 sm:h-24 mr-5 ring-4 ring-rose-100" />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm flex items-center">
                      <MapPin className="w-3 h-3 mr-1" />
                      {testimonial.location}
                    </p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed italic">"{testimonial.text}"</p>
                <div className="flex text-yellow-400 mt-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
              </Card>
            ))}
          </div>
          
          {/* Additional testimonials row */}
          {testimonials.length > 3 && (
            <div className="grid md:grid-cols-2 gap-8 mt-8">
              {testimonials.slice(3).map((testimonial, index) => (
                <Card key={index + 3} className="p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                  <div className="flex items-center mb-4">
                    <PersonAvatar personId={testimonial.personId} className="w-20 h-20 sm:w-24 sm:h-24 mr-5 ring-4 ring-rose-100" />
                    <div>
                      <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                      <p className="text-gray-600 text-sm flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />
                        {testimonial.location}
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed italic">"{testimonial.text}"</p>
                  <div className="flex text-yellow-400 mt-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}

          <div className="mt-12 text-center" data-testid="share-story-cta">
            <p className="text-gray-700">Met someone on FlameLink? We'd love to hear your real story.</p>
            <button type="button" onClick={onGetStarted} className="mt-2 text-rose-600 font-semibold hover:text-rose-700 underline-offset-4 hover:underline" data-testid="share-story-cta-btn">
              Sign in and share your story from Settings →
            </button>
            <p className="text-xs text-gray-500 mt-1">Only published with your written consent.</p>
          </div>
        </div>
      </section>

      <LandingPremiumSection onSeePricing={() => onFootnoteClick('premium')} />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 via-pink-600 to-orange-600 relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10 grid lg:grid-cols-[1fr_auto] gap-10 items-center max-w-5xl">
          <PhotoPanel ids={['girl96', 'kogal2']} tone="ruby" className="order-2 lg:order-1 max-w-md mx-auto w-full" heights="h-[240px] sm:h-[300px]" testId="cta-photo-panel" />
          <div className="order-1 lg:order-2 text-center lg:text-left lg:max-w-xl">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Find Your Perfect Match?
          </h2>
          <p className="text-xl text-pink-100 mb-8 max-w-2xl mx-auto">
            Join singles who are looking for something real. Verified profiles, thoughtful matching, and private messaging — all in one place.
          </p>
          
          <Button
            onClick={onGetStarted}
            className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-2xl"
          >
            <Flame className="w-5 h-5 mr-2" />
            Start Your Journey Today
          </Button>
          
          <p className="text-pink-200 mt-4 text-sm">
            Free to join • No credit card required • Available in 50+ global cities
          </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <BrandLogo variant="script" className="h-12 w-auto" sizes="64px" testId="footer-brand-logo" />
                <span className="text-xl font-bold bg-gradient-to-r from-amber-300 via-pink-300 to-rose-300 bg-clip-text text-transparent">FlameLink</span>
              </div>
              <p className="text-gray-400">
                The future of dating is here. Find meaningful connections with AI-powered matching.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Features</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Smart Matching</li>
                <li>Video Profiles</li>
                <li>Verified Users</li>
                <li>Compatibility Scoring</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button 
                    onClick={() => onFootnoteClick('about')}
                    className="hover:text-white transition-colors"
                  >
                    About Us
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => onFootnoteClick('privacy')}
                    className="hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => onFootnoteClick('terms')}
                    className="hover:text-white transition-colors"
                  >
                    Terms of Service
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => onFootnoteClick('contact')}
                    className="hover:text-white transition-colors"
                  >
                    Contact
                  </button>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button 
                    onClick={() => onFootnoteClick('cookie-policy')}
                    className="hover:text-white transition-colors"
                  >
                    Cookie Policy
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => onFootnoteClick('data-processing')}
                    className="hover:text-white transition-colors"
                  >
                    Data Processing Agreement
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => onFootnoteClick('age-verification')}
                    className="hover:text-white transition-colors"
                  >
                    Age Verification Policy
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => onFootnoteClick('refund-policy')}
                    className="hover:text-white transition-colors"
                  >
                    Refund Policy
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => onFootnoteClick('premium')}
                    className="hover:text-white transition-colors"
                  >
                    Premium Pricing
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => onFootnoteClick('legal')}
                    className="hover:text-white transition-colors"
                  >
                    Legal Center
                  </button>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button 
                    onClick={() => onFootnoteClick('help')}
                    className="hover:text-white transition-colors"
                  >
                    Help Center
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => onFootnoteClick('safety')}
                    className="hover:text-white transition-colors"
                  >
                    Safety Tips
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => onFootnoteClick('guidelines')}
                    className="hover:text-white transition-colors"
                  >
                    Community Guidelines
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => onFootnoteClick('report')}
                    className="hover:text-white transition-colors"
                  >
                    Report Issues
                  </button>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 FlameLink. All rights reserved. Made with ❤️ for meaningful connections.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;